import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, ContentSection, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { AddressSearchBar } from '../../../../components/AddressSearchBar';
import { creditApplicationSelectors } from '../../../../services/redux/features/creditApplication.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { AddressLine1, AddressLine2, City, Country, ZipCode } from '../../utils/fieldsBusinessCustomer';
import {
  CustomerDetailsFormGroupForms,
  CustomerDetailsFormGroupType,
  patchCreditApplicationAndUpdateState,
  useFormSectionHelpers,
} from '../utils';
import { AddressDataFormType, getAddressDataFormInitialValues } from './initialValues';
import { addressDataFormRequestMapper } from './requestMapper';
import { getAddressDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsFormGroupForms<'business'>;
  formGroup: CustomerDetailsFormGroupType;
}

export const AddressDataForm: FunctionComponent<Props> = ({ formName, formGroup }) => {
  const [initialValues, setInitialValues] = useState({} as AddressDataFormType);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<AddressDataFormType>>();

  const creditApplicantCustomerData = useSelector(creditApplicationSelectors.selectCreditApplicantCustomerData) as
    | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
    | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    setInitialValues(getAddressDataFormInitialValues(creditApplicantCustomerData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplicantCustomerData]);

  useEffect(() => {
    setValidationSchema(getAddressDataFormValidationSchema(t));
  }, [t]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    formGroup,
    formName
  );

  const onSubmit = (values: AddressDataFormType) => {
    dispatch(loadingActions.setLoading(true));
    patchCreditApplicationAndUpdateState(addressDataFormRequestMapper(values, creditApplicantCustomerData))
      .then(onFormSubmitSuccess)
      .catch(navigate.error)
      .finally(() => dispatch(loadingActions.setLoading(false)));
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);

  return (
    <ContentSection.ComponentWrapper>
      <FormSection title={t('step4:heading:address')} {...formSectionProps}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {isCreditApplicationDataReady && (
            /* eslint-disable-next-line no-use-before-define */
            <FormContent onFormInitialValuesChange={onFormInitialValuesChange} />
          )}
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
}

const FormContent: FunctionComponent<FormContentProps> = ({ onFormInitialValuesChange }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, submitForm, initialValues, validateForm } = useFormikContext<AddressDataFormType>();
  const [addressSearchValue, setAddressSearchValue] = useState('');

  useEffect(() => {
    onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleChange = () => '';

  const formProps = {
    values,
    setFieldValue,
  } as React.PropsWithChildren<any>;

  return (
    <Fieldset className="u-pt-none">
      <Fieldset.Row>
        <Layout.Item default="1/1" s="1/1">
          <AddressSearchBar t={t} input={addressSearchValue} onChange={setAddressSearchValue} formProps={formProps} />
        </Layout.Item>
        <Layout>
          <Layout.Item default="1/2" s="1/1">
            <AddressLine1 t={t} onManualAddressInput={() => setAddressSearchValue('')} handleChange={handleChange} />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <AddressLine2 t={t} onManualAddressInput={() => setAddressSearchValue('')} handleChange={handleChange} />
          </Layout.Item>
          <Layout.Item default="1/3" s="1/1">
            <ZipCode t={t} onManualAddressInput={() => setAddressSearchValue('')} handleChange={handleChange} />
          </Layout.Item>
          <Layout.Item default="1/3" s="1/1">
            <City t={t} onManualAddressInput={() => setAddressSearchValue('')} handleChange={handleChange} />
          </Layout.Item>
          <Layout.Item default="1/3" s="1/1">
            <Country />
          </Layout.Item>
          <ButtonContainer right>
            <Layout right>
              <Layout.Item default="1/3" m="2/5" s="2/3">
                <Button full type="submit" onClick={submitForm}>
                  {t('step3:navigation:continue')}
                </Button>
              </Layout.Item>
            </Layout>
          </ButtonContainer>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
